// @owners { team: patients }
import { COLORS, TYPOGRAPHY } from '@alto/design-library-tokens';
import { ActionSheetContext, Button, Card, Column, InlineAlert, LgPadding, Row } from '@alto/design-system';
import { Experimentation } from '@alto/experimentation';
import { FSAIndividualReceiptAlert } from '@alto/features';
import { useQueryClient } from '@tanstack/react-query';
import React, { useContext } from 'react';
import styled from 'styled-components';
import { clearPaymentMethodError, deletePaymentMethod } from '~shared/actions/paymentMethods';
import { useIsFeatureDisabledForCompany } from '~shared/hooks';
import { queries } from '~shared/queries/query-keys';
import { useGetPaymentMethods } from '~shared/queries/query-keys/paymentMethods';
import { getPaymentBreakdowns } from '~shared/reducers/paymentMethods';
import { useDispatchShared, useSelectorShared } from '~shared/store';
import { type PaymentMethod } from '~shared/types';
import { AchPaymentMethods } from './AchPaymentMethods';
import { CreditCardPayments } from './CreditCardPayments';
import { Breadcrumbs } from '~web/components/Breadcrumbs';
import { ContentWrapper, Main, PageBackground } from '~web/components/Page';
import { PageHeader } from '~web/components/PageHeader';
import { CreditCardActionSheet } from '~web/features/payments/components/CreditCardActionSheet';
import { FSAReceiptSection } from '~web/features/payments/components/FSAReceiptSection';

const FormHelper = styled.div`
  font-family: ${TYPOGRAPHY.FONT.BODY.REGULAR};
  font-size: ${TYPOGRAPHY.TEXT.DETAIL.LG.px};
  color: ${COLORS.PALETTE.PRIMARY.DEFAULT};
  letter-spacing: 0.49px;
  margin-bottom: 0;
  .errored & {
    color: ${COLORS.PALETTE.DANGER.DEFAULT};
  }
`;

const BREADCRUMBS = [
  {
    text: 'Account',
    to: '/me',
  },
  {
    text: 'Payments and Receipts',
  },
];

export const ProfilePaymentMethods = () => {
  const { setActiveActionSheet } = useContext(ActionSheetContext);
  const { value: isAddApplePayDisabled } = Experimentation.useFeatureFlag('apple_pay_enabled');
  const { isFeatureDisabled: shouldDisableLinkedAccount, isLoading: isFeatureFlagLoading } =
    useIsFeatureDisabledForCompany('plaid_bank_account');
  const queryClient = useQueryClient();
  const dispatch = useDispatchShared();
  const loading = useSelectorShared(
    (state) => state.ui.loading.createPaymentMethodLoading || state.ui.loading.deletePaymentMethodLoading,
  );
  const { error: fetchPaymentMethodError, paymentMethods } = useGetPaymentMethods();
  const reduxPaymentMethodError = useSelectorShared((state) => {
    const { createPaymentMethodError, deletePaymentMethodError } = state.ui.errors;
    return createPaymentMethodError || deletePaymentMethodError;
  });
  const paymentMethodError = reduxPaymentMethodError || fetchPaymentMethodError;
  const { achPaymentMethods, creditCardPaymentMethods, nativePaymentMethods } = getPaymentBreakdowns(paymentMethods);

  const applePayPaymentMethods = nativePaymentMethods.filter((pm) => pm.details?.native_pay_type === 'apple_pay');
  const googlePayPaymentMethods = nativePaymentMethods.filter((pm) => pm.details?.native_pay_type === 'google_pay');
  const handleDeletePaymentMethod = async (paymentMethod: PaymentMethod) => {
    const success = await dispatch(deletePaymentMethod(paymentMethod));
    if (success) {
      queryClient.invalidateQueries({ queryKey: queries.paymentMethods.fetchAll._def });
    }
  };

  return (
    <PageBackground>
      <ContentWrapper>
        <Column>
          <Breadcrumbs breadcrumbs={BREADCRUMBS} />
          <PageHeader>Payments and Receipts</PageHeader>
        </Column>
        <Main>
          {paymentMethodError ? <InlineAlert type="warning">{paymentMethodError.message}</InlineAlert> : null}
          <Card title="Credit Cards">
            <CreditCardPayments
              creditCardPaymentMethods={creditCardPaymentMethods}
              deletePaymentMethod={handleDeletePaymentMethod}
            />
            <Row right>
              <LgPadding>
                <Button
                  type="secondary"
                  onPress={() => {
                    dispatch(clearPaymentMethodError());
                    setActiveActionSheet(<CreditCardActionSheet />);
                  }}
                  small
                  label="Add new card"
                  accessibilityLabel="Press to add credit card"
                />
              </LgPadding>
            </Row>
          </Card>
          {!shouldDisableLinkedAccount && !isFeatureFlagLoading && (
            <Card
              title="Linked Accounts"
              description={
                achPaymentMethods.length === 0
                  ? 'Link your bank account to automatically save 1% off every order that is paid without insurance.'
                  : undefined
              }
            >
              <AchPaymentMethods
                paymentMethods={achPaymentMethods}
                deletePaymentMethod={handleDeletePaymentMethod}
              />
              {loading ? <FormHelper>Loading&hellip;</FormHelper> : null}
            </Card>
          )}
          {!isAddApplePayDisabled && applePayPaymentMethods.length > 0 && (
            <Card title="Apple Pay">
              <CreditCardPayments
                creditCardPaymentMethods={applePayPaymentMethods}
                deletePaymentMethod={handleDeletePaymentMethod}
              />
            </Card>
          )}
          {googlePayPaymentMethods.length > 0 && (
            <Card title="Google Pay">
              <CreditCardPayments
                creditCardPaymentMethods={googlePayPaymentMethods}
                deletePaymentMethod={handleDeletePaymentMethod}
              />
            </Card>
          )}
          <Card
            title="Annual Receipts"
            description="Download itemized receipts for FSA/HSA claims, taxes, or other reimbursement purposes."
          >
            <FSAReceiptSection />
            <FSAIndividualReceiptAlert />
          </Card>
        </Main>
      </ContentWrapper>
    </PageBackground>
  );
};
